import languageStore, { LangId } from "~/stores/language";
import tagStore, { TagId } from "~/stores/tags";
import {
  Access,
  accessToText,
  DateRange,
  DocInstance,
  docInstanceLabel,
  DocSeriesId,
  Document,
  OrganizationalUnitId,
} from "~/document";
import topicStore, { TopicId } from "~/stores/topics";
import countryStore, { CountryId } from "~/stores/country";
import docTypeStore, { DocTypeId } from "~/stores/documentTypes";
import docSeriesStore from "~/stores/docSeries";
import projectStore, { ProjectId } from "~/stores/projects";
import ownerStore, { OwnerId } from "~/stores/owners";
import officeStore, { OfficeId } from "~/stores/offices";
import divisionStore, { DivisionId } from "~/stores/divisions";
import { CustomFormatter, SearchConverter, searchConverter } from "~/util/searchConverter";
import moment from "moment";
import { dateToAPIString, formatDateReadable } from "~/util";
import { PUB_DATE_FORMAT } from "./constants";
import { SavedSearchInfo } from "./stores/savedSearch";
import { isSet } from "lodash";
import { MeetingId } from "~/stores/agendaItemDocs";
import organizationalUnitStore from "~/stores/organizationalUnits";

type OnlyArraySubProperties<T> = {
  [Property in keyof T as Exclude<Property, unknown[]>]: T[Property];
};

type KeyofOnlyArrays<T> = keyof OnlyArraySubProperties<T>;

type addPrefix<TKey, TPrefix extends string> = TKey extends string ? `${TPrefix}${TKey}` : never;

type addSuffix<TKey, TPrefix extends string> = TKey extends string ? `${TKey}${TPrefix}` : never;

type MatchStuff = {
  [K in keyof Document]: Document[K];
} & {
  [K in addSuffix<keyof Partial<Document>, ".like">]: any;
} & {
  [K in addSuffix<keyof Partial<Document>, ".std">]: any;
};

type SearchFilter = {
  match?: Partial<MatchStuff>;
  range?: SearchFilter;
  or?: SearchFilter[];
  and?: SearchFilter[];
};

type APISearchQuery = URLSearchParams;

enum SortOrder {
  Descending = "desc",
  Ascending = "asc",
}

interface SortBy {
  field: keyof Document;
  order: SortOrder;
}

export type SearchQueryJSON = Partial<Record<keyof SearchQuery, { label: string; value: string | number | string[] }>>;

const ADVANCED_SEARCH_FILTERS: Set<keyof SearchQuery> = new Set([
  "languages",
  "tags",
  "topics",
  "docTypes",
  "countries",
  "author",
  "protocol",
  "projects",
  "access",
  "instances",
  "publication_date",
  "expiration_date",
  "third_types",
  "release_date",
  "owners",
  "number",
  "offices",
  "divisions",
  "organizational_units",
  "searchAnywhere",
  "last_modified_user",
  "is_title_visible_to_public",
]);

// Represents the document search query. Can be converted to JSON, readable JSON, API call, etc.
//
// Uses the Builder pattern. In order to modify the query, use `query.set(key, value)`. This will
// return a new instance of the query with one value modified. `removeSimpleFilter` and such can be used
// to remove values.
class SearchQuery {
  title = "";
  author = "";
  protocol = "";
  number = "";
  abstract = "";
  comment?: string = "";
  pdf_option?: number = 1;
  languages: LangId[] = [];
  topics: Set<TopicId> = new Set([]);
  tags: TagId[] = [];
  countries: CountryId[] = [];
  projects: ProjectId[] = [];
  access: Access[] = [];
  docTypes: Set<DocTypeId> = new Set([]);
  meeting_codes: MeetingId[] = [];
  instances: DocInstance[] = [];
  organizational_units: OrganizationalUnitId[] = [];
  third_types: DocSeriesId[] = [];
  expiration_date: DateRange | undefined = undefined;
  publication_date: DateRange | undefined = undefined;
  release_date: DateRange | undefined = undefined;
  owners: OwnerId[] = [];
  offices: OfficeId[] = [];
  divisions: DivisionId[] = [];
  searchAnywhere = "";
  last_modified_user = "";
  note?: string;
  is_title_visible_to_public?: boolean;

  page = 0;
  pageSize = 10;

  sortByField = "-publication_date";
  sortByOrder: SortOrder = SortOrder.Descending;

  constructor(qq?: Partial<SearchQuery>) {
    if (qq) {
      for (let [propName, propValue] of Object.entries(qq)) {
        (this as any)[propName] = propValue;
      }
    }
  }

  static fromQueryString(queryString: URLSearchParams) {
    return searchQueryQueryConverter.from(queryString);
  }

  isValid(): boolean {
    return !this.isEmpty();
  }

  isTitlePresent(): boolean {
    return !!this.title && this.title.trim().length !== 0;
  }

  isOneOfTheFieldsSelected(): boolean {
    return this.isTitlePresent() || this.isThereAnyAdvancedFiltersApplied();
  }

  toJSON() {
    return { ...this, topics: Array.from(this.topics), docTypes: Array.from(this.docTypes) };
  }

  toFullJSON(): SearchQueryJSON {
    return {
      title: {
        label: "Title",
        value: this.title,
      },
      languages: {
        label: "Languages",
        value: this.languages.map((langId) => {
          if (!(langId in languageStore.loadedLanguages)) return "";
          return languageStore.loadedLanguages[langId].text;
        }),
      },
      tags: { label: "Tags", value: this.tags.map((tagId) => tagStore.f.items[tagId]?.label) },
      author: { label: "Author", value: this.author },
      topics: {
        label: "Topics",
        value: Array.from(this.topics)
          .map((id) => {
            return topicStore.topicsById.get(id as number)?.label || null;
          })
          .filter((a) => a !== null) as string[],
      },
      docTypes: {
        label: "Document types",
        value: Array.from(this.docTypes)
          .map((id) => {
            return docTypeStore.docTypesById.get(id)?.label || null;
          })
          .filter((a) => a !== null) as string[],
      },
      countries: {
        label: "Geographical coverage",
        value: Array.from(this.countries).map((id) => {
          const sid = String(id);
          return countryStore.f.items[sid]?.name;
        }),
      },
      owners: {
        label: "Owners",
        value: Array.from(this.owners).map((id) => {
          const sid = String(id);
          return ownerStore.f.items[sid]?.username;
        }),
      },
      protocol: { label: "Document symbol", value: this.protocol },
      number: { label: "Document symbol", value: this.number },
      projects: { label: "Project number", value: this.projects },
      meeting_codes: { label: "Meeting codes", value: this.meeting_codes },
      access: { label: "Access level", value: this.access.map(accessToText) },
      searchAnywhere: { label: "Search Anywhere", value: this.searchAnywhere },
      last_modified_user: { label: "Last updated by", value: this.last_modified_user },
      instances: {
        label: "Instances",
        value: this.instances.map((id) => {
          return docInstanceLabel[id];
        }),
      },
      third_types: {
        label: "Series",
        value: this.third_types.map((id) => {
          return docSeriesStore.loaded[id].label;
        }),
      },
      publication_date: {
        label: "Publication date",
        value: this.publication_date
          ? `${moment(this.publication_date[0]).format("DD-MM-YYYY")} -> ${moment(this.publication_date[1]).format(
              "DD-MM-YYYY"
            )}`
          : "",
      },
      offices: {
        label: "Offices",
        value: Array.from(this.offices).map((id) => {
          const sid = String(id);
          return officeStore.f.items[sid]?.name;
        }),
      },
      divisions: {
        label: "Divisions",
        value: Array.from(this.divisions).map((id) => {
          const sid = String(id);
          return divisionStore.f.items[sid]?.name;
        }),
      },
      organizational_units: {
        label: "Organizational units",
        value: Array.from(this.organizational_units).map((id) => {
          const sid = String(id);
          return organizationalUnitStore.f.items[sid]?.name;
        }),
      },
    };
  }

  // Convert to a API format to be used in the /search-restful/ endpoint
  toAPIFormat(): APISearchQuery {
    const qry = new URLSearchParams();

    const addRangeParamIfPresent = (paramName: keyof typeof this) => {
      const val: any[] = this[paramName] as any;
      if (!val || val.length === 0) return;
      const gte = dateToAPIString(val[0]);
      if (val.length >= 2) {
        const lte = dateToAPIString(val[1]);
        qry.set(`${String(paramName)}__range`, [gte, lte].join("__"));
      } else {
        qry.set(`${String(paramName)}__gte`, gte);
      }
    };

    const addOrParam = (name: string, arr: any[]) => {
      if (arr.length !== 0) {
        qry.set(`${name}__in_all`, arr.join("__"));
      }
    };

    addOrParam("language", this.languages);
    addOrParam("office", this.offices);
    if (this.owners.length !== 0) {
      const usernames = this.owners
        .map((id) => ownerStore.f.items[id as any]?.username)
        .filter((name): name is string => !!name);
      if (usernames.length !== 0) {
        qry.set("owner__in", usernames.join("__"));
      }
    }
    addOrParam("access", this.access);
    addOrParam("third_type", this.third_types);
    qry.set("author__contains", this.author);

    if (this.number) qry.set("number", this.number);

    addOrParam("instance", this.instances);

    addRangeParamIfPresent("publication_date");
    addRangeParamIfPresent("expiration_date");
    addRangeParamIfPresent("release_date");

    qry.set("search", this.title);

    if (this.searchAnywhere && this.searchAnywhere.trim().length !== 0) {
      qry.set("search", this.searchAnywhere);
    }

    if (this.last_modified_user && this.last_modified_user.trim().length !== 0) {
      qry.set("last_modified_user", this.last_modified_user);
    }

    if (this.organizational_units && this.organizational_units.length !== 0) {
      addOrParam("organizational_unit", this.organizational_units);
    }

    addOrParam("tags", this.tags);

    if (this.topics && this.topics.size !== 0) {
      addOrParam("topics", Array.from(this.topics));
    }
    if (this.meeting_codes.length) {
      addOrParam("codes", this.meeting_codes);
    }
    if (this.docTypes && this.docTypes.size !== 0) {
      addOrParam("types", Array.from(this.docTypes));
    }
    if (this.countries && this.countries.length !== 0) {
      addOrParam("countries", Array.from(this.countries));
    }

    // Sorting params
    qry.set("ordering", this.sortByField);

    return qry;
  }

  toQueryString() {
    return searchQueryQueryConverter.to({ ...this });
  }

  set<K extends keyof this>(key: K, value: (typeof this)[K]) {
    this[key] = value;
    return new SearchQuery(this);
  }

  remove(
    fieldName: keyof this,
    params: {
      key?: string;
      idx?: number;
    }
  ) {
    if (this[fieldName] instanceof Array) {
      return this.removeArrayFilterPart(fieldName as KeyofOnlyArrays<this>, params.idx);
    }
    if (isSet(this[fieldName])) {
      let itemToRemove = params.key;
      if (!itemToRemove && !isNaN(+params.idx)) {
        itemToRemove = Array.from(this[fieldName] as Set<any>)[params.idx];
      }
      return this.removeSetFilterPart(fieldName, itemToRemove);
    }
    return this.removeSimpleFilter(fieldName);
  }

  removeArrayFilterPart(key: KeyofOnlyArrays<this>, idx: number) {
    (this[key] as any).splice(idx, 1);
    return new SearchQuery(this);
  }

  removeSetFilterPart(fieldName: keyof this, key: string) {
    (this[fieldName] as any as Set<any>).delete(key);
    return new SearchQuery(this);
  }

  removeSimpleFilter(key: keyof this) {
    delete this[key];
    return new SearchQuery(this);
  }

  *forAllAppliedAdvancedSearchFilters() {
    for (let field of ADVANCED_SEARCH_FILTERS) {
      const val = this[field];
      if (val instanceof Array) {
        if (val.length !== 0) yield val;
      } else if (val instanceof Set) {
        if (val.size !== 0) yield val;
      } else if (val) {
        yield val;
      }
    }
  }

  isEmpty() {
    return !this.isTitlePresent() && !this.isThereAnyAdvancedFiltersApplied();
  }

  isThereAnyAdvancedFiltersApplied() {
    for (let val of this.forAllAppliedAdvancedSearchFilters()) {
      return true;
    }
    return false;
  }

  numOfAppliedSearchFilters() {
    let k = 0;
    for (let val of this.forAllAppliedAdvancedSearchFilters()) {
      ++k;
    }
    return k;
  }

  isAdvancedFilterField(field: keyof SearchQuery) {
    return ADVANCED_SEARCH_FILTERS.has(field);
  }

  // Will make API requests for all missing data found in this query
  requestAllMissingDataForQuery = async () => {
    const promises: Promise<any>[] = [];
    if (this.languages.length !== 0) {
      promises.push(languageStore.loadLanguages());
    }
    if (this.countries.length !== 0) {
      promises.push(countryStore.f.loadIds(this.countries));
    }
    if (this.tags.length !== 0) {
      promises.push(tagStore.f.loadIds(this.tags));
    }
    if (this.docTypes.size !== 0) {
      promises.push(docTypeStore.loadDocTypes());
    }
    if (this.topics.size !== 0) {
      promises.push(topicStore.loadTopics());
    }
    if (this.projects.length !== 0) {
      promises.push(projectStore.f.loadIds(this.projects));
    }
    if (this.offices.length !== 0) {
      promises.push(officeStore.f.loadIds(this.offices));
    }
    if (this.third_types.length != 0) {
      promises.push(docSeriesStore.f.loadIds(this.third_types));
    }
    if (this.owners.length !== 0) {
      promises.push(ownerStore.f.loadIds(this.owners));
    }
    if (this.divisions.length !== 0) {
      promises.push(divisionStore.f.loadIds(this.divisions));
    }
    if (this.organizational_units.length !== 0) {
      promises.push(organizationalUnitStore.f.loadIds(this.organizational_units));
    }
    return await Promise.all(promises);
  };

  static fromSavedSearch = (savedSearch: SavedSearchInfo): SearchQuery => {
    let sq = new SearchQuery(savedSearch.complex_search_value);
    sq = sq.set("title", savedSearch.search_value);
    return sq;
  };
}

// This is a custom converter for dates
const dateRangeConverter: CustomFormatter<DateRange> = {
  to: (val) => {
    if (val.length <= 0) return null;
    const _from = val[0] ? formatDateReadable(val[0]) : null;
    const _to = val[1] ? formatDateReadable(val[1]) : null;
    return [_from, _to].filter((a) => a !== null).join(",");
  },
  from: (val) => {
    const r = val.split(",").filter((item) => !!item);
    let res: any = r.map((item) => moment(item, PUB_DATE_FORMAT).toDate());
    return res;
  },
};

// This is used to convert back and forth between search query URL representation and SearchQuery instances
// to communicate between app pages
const searchQueryQueryConverter = searchConverter<SearchQuery>(
  {
    title: SearchConverter.String,
    searchAnywhere: SearchConverter.String,
    last_modified_user: SearchConverter.String,
    author: SearchConverter.String,
    protocol: SearchConverter.String,
    number: SearchConverter.String,
    is_title_visible_to_public: SearchConverter.NumberToBoolean,

    languages: SearchConverter.Array,
    tags: SearchConverter.Array,
    topics: SearchConverter.NumberSet,
    countries: SearchConverter.Array,
    projects: SearchConverter.Array,
    access: SearchConverter.Array,
    docTypes: SearchConverter.NumberSet,
    instances: SearchConverter.Array,
    third_types: SearchConverter.Array,
    meeting_codes: SearchConverter.Array,

    expiration_date: dateRangeConverter,
    publication_date: dateRangeConverter,
    release_date: dateRangeConverter,

    owners: SearchConverter.Array,
    offices: SearchConverter.Array,
    divisions: SearchConverter.Array,
    organizational_units: SearchConverter.Array,

    sortByField: SearchConverter.String,
    sortByOrder: SearchConverter.String,

    page: SearchConverter.Number,
    pageSize: SearchConverter.Number,
  },
  () => new SearchQuery()
);

export default SearchQuery;
